import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle, MonitorSmartphone, Network, Server, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils.ts'
import { apiClient } from '@/lib/api.ts'
import { getAllNinjaDevices, type NinjaDevice, type NinjaOrgDevices } from '@/api/ninjaAdapter.ts'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  Select,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type ColumnDef,
} from '@/components/ui'

// ─── Helpers ────────────────────────────────────────────────────────────────

function apiErrMsg(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message ?? e?.message ?? 'Unbekannter Fehler'
}

function apiErrCode(err: unknown): string | undefined {
  const e = err as { response?: { data?: { code?: string } } }
  return e?.response?.data?.code
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface UnifiStatus {
  configured: boolean
  reachable: boolean
  error: string | null
  sites: string[]
}

interface Site {
  id: string
  name: string
  desc?: string
}

interface Device {
  mac: string
  name?: string
  model?: string
  ip?: string
  type: 'uap' | 'usw' | 'ugw' | 'udm'
  state: 0 | 1
  status: 'online' | 'offline'
  uptime?: number
  version?: string
  updateAvailable?: boolean
  num_sta?: number
}

interface DeviceWithSite extends Device {
  siteName?: string
  siteId?: string
}

interface Alert {
  _id: string
  key: string
  msg: string
  time: number
  datetime: string
  site_id: string
  device_mac?: string
  subsystem: string
  archived: boolean
  severity: 'critical' | 'warning' | 'info'
}

interface AlertsData {
  alerts: Alert[]
  total: number
  bySeverity: { critical: number; warning: number; info: number }
}

interface ClientsCountData {
  count: number
  siteId: string
}

interface SiteOverview {
  id: string
  name: string
  desc?: string
  devices: Device[]
  alerts: Alert[]
  clientsCount: number
  error?: string
  devicesWarning?: string
}

interface AllSitesOverviewPage {
  sites: SiteOverview[]
  totals: {
    devices: number
    online: number
    clients: number
    alerts: number
    bySeverity: { critical: number; warning: number; info: number }
  }
  page: number
  pageSize: number
  totalSites: number
  hasMore: boolean
}

// Merged view across all loaded pages
interface AllSitesOverview {
  sites: SiteOverview[]
  totals: AllSitesOverviewPage['totals']
  hasMore: boolean
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEVICE_TYPE_LABEL: Record<string, string> = {
  uap: 'UAP',
  usw: 'USW',
  ugw: 'UGW',
  udm: 'UDM',
}

const ALL_SITES_VALUE = '__all__'

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  if (d > 0) return `${d}d ${h}h`
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function getAlarmForDevice(device: Device, alerts: Alert[]): Alert | undefined {
  return alerts.find((a) => a.device_mac === device.mac)
}

// ─── KPI Tile ────────────────────────────────────────────────────────────────

interface KpiTileProps {
  icon: ReactNode
  label: string
  value: ReactNode
  iconClass?: string
}

function KpiTile({ icon, label, value, iconClass }: KpiTileProps) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-card">
      <div className={cn('h-5 w-5 shrink-0', iconClass)}>{icon}</div>
      <span className="text-caption text-gray-500">{label}</span>
      <span className="text-h3 font-semibold text-gray-900">{value}</span>
    </div>
  )
}

// ─── Device Detail Sheet ─────────────────────────────────────────────────────

interface DeviceSheetProps {
  device: DeviceWithSite | undefined
  alerts: Alert[]
}

function DeviceSheet({ device, alerts }: DeviceSheetProps) {
  if (!device) return null

  const deviceAlerts = alerts.filter((a) => a.device_mac === device.mac)

  return (
    <>
      <SheetClose />
      <SheetHeader>
        <div className="flex items-center gap-2 pr-8">
          <span
            className={cn(
              'h-2.5 w-2.5 shrink-0 rounded-full',
              device.status === 'online' ? 'bg-green-500' : 'bg-red-400',
            )}
          />
          <SheetTitle>{device.name ?? device.mac}</SheetTitle>
          <StatusBadge status={device.status} size="sm" />
        </div>
        <p className="text-caption text-gray-500">
          {[device.model, DEVICE_TYPE_LABEL[device.type], device.ip].filter(Boolean).join(' · ')}
        </p>
      </SheetHeader>

      <SheetBody className="space-y-5">
        <div>
          <p className="mb-2 text-small font-semibold text-gray-700">Gerätedetails</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-small">
            {device.siteName && (
              <>
                <dt className="text-gray-500">Kunde</dt>
                <dd className="text-gray-900">{device.siteName}</dd>
              </>
            )}
            {device.model && (
              <>
                <dt className="text-gray-500">Modell</dt>
                <dd className="font-mono text-gray-900">{device.model}</dd>
              </>
            )}
            <dt className="text-gray-500">Typ</dt>
            <dd>
              <Badge variant="info" size="sm">
                {DEVICE_TYPE_LABEL[device.type] ?? device.type}
              </Badge>
            </dd>
            {device.ip && (
              <>
                <dt className="text-gray-500">IP</dt>
                <dd className="font-mono text-gray-900">{device.ip}</dd>
              </>
            )}
            <dt className="text-gray-500">MAC</dt>
            <dd className="font-mono text-gray-900">{device.mac}</dd>
            <dt className="text-gray-500">Firmware</dt>
            <dd>
              {device.version ? (
                <Badge variant={device.updateAvailable ? 'warning' : 'success'} size="sm">
                  {device.version}
                  {device.updateAvailable ? ' (veraltet)' : ''}
                </Badge>
              ) : (
                <span className="text-gray-400">–</span>
              )}
            </dd>
            {device.uptime !== undefined && (
              <>
                <dt className="text-gray-500">Uptime</dt>
                <dd className="text-gray-900">{formatUptime(device.uptime)}</dd>
              </>
            )}
          </dl>
        </div>

        {deviceAlerts.length > 0 && (
          <div>
            <p className="mb-2 text-small font-semibold text-gray-700">
              Alarme ({deviceAlerts.length})
            </p>
            <div className="space-y-2">
              {deviceAlerts.map((alert) => (
                <div
                  key={alert._id}
                  className={cn(
                    'flex items-start gap-3 rounded-md border px-3 py-2',
                    alert.severity === 'critical' && 'border-red-200 bg-red-50',
                    alert.severity === 'warning' && 'border-amber-200 bg-amber-50',
                    alert.severity === 'info' && 'border-blue-200 bg-blue-50',
                  )}
                >
                  <div
                    className={cn(
                      'mt-0.5 h-2 w-2 shrink-0 rounded-full',
                      alert.severity === 'critical' && 'bg-red-500',
                      alert.severity === 'warning' && 'bg-yellow-500',
                      alert.severity === 'info' && 'bg-blue-500',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-small font-medium text-gray-900">{alert.msg}</p>
                    <p className="text-caption text-gray-500">
                      {new Date(alert.datetime).toLocaleString('de-DE')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetBody>
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface UnifiDebugData {
  hosts: Array<Record<string, unknown>>
  sites: Array<Record<string, unknown>>
  computedSites: Array<{
    siteId: string
    internalId: string
    rawHostId: string
    proxyHostId: string
    macOnlyHostId: string | null
  }>
  proxyPathTest?: Array<{
    path: string
    label: string
    result: 'ok' | 'error'
    status?: number
    detail: string
  }>
}

export function NetworkOverviewPage() {
  // null = "Alle Kunden" (Gesamtuebersicht), string = specific site ID
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const [openDeviceId, setOpenDeviceId] = useState<string | null>(null)
  const [showDebug, setShowDebug] = useState(false)

  const isAllSites = selectedSiteId === null

  const { data: unifiStatus } = useQuery<UnifiStatus>({
    queryKey: ['network', 'status'],
    queryFn: () => apiClient.get('/network/status').then((r) => r.data),
    refetchInterval: 60_000,
    retry: false,
  })

  const {
    data: sites = [],
    isLoading: sitesLoading,
    isError: sitesError,
    error: sitesErrorObj,
  } = useQuery<Site[]>({
    queryKey: ['network', 'sites'],
    queryFn: () => apiClient.get('/network/sites').then((r) => r.data),
    refetchInterval: 60_000,
    retry: 1,
  })

  // Paginated all-sites overview — loads first page immediately, fetches remaining pages in background
  const {
    data: allOverviewPages,
    isLoading: allOverviewLoading,
    isError: allOverviewError,
    error: allOverviewErrorObj,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<AllSitesOverviewPage>({
    queryKey: ['network', 'all-overview'],
    queryFn: ({ pageParam }) =>
      apiClient
        .get('/network/all-overview', { params: { page: pageParam, pageSize: 10 } })
        .then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage: AllSitesOverviewPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: isAllSites,
    refetchInterval: 60_000,
    retry: 1,
  })

  // Auto-load all remaining pages in background once first page arrives
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Merge all loaded pages into a single virtual overview
  const allOverview = useMemo<AllSitesOverview | undefined>(() => {
    if (!allOverviewPages) return undefined
    const pages = allOverviewPages.pages
    const lastPage = pages[pages.length - 1]
    return {
      sites: pages.flatMap((p) => p.sites),
      totals: lastPage.totals,
      hasMore: lastPage.hasMore,
    }
  }, [allOverviewPages])

  // Per-site queries (only active when a specific site is selected)
  const {
    data: siteDevices = [],
    isLoading: devicesLoading,
    isError: devicesError,
    error: devicesErrorObj,
  } = useQuery<Device[]>({
    queryKey: ['network', 'devices', selectedSiteId],
    queryFn: () =>
      apiClient.get(`/network/sites/${selectedSiteId}/devices`).then((r) => r.data),
    enabled: !isAllSites,
    refetchInterval: 60_000,
    retry: 1,
  })

  const { data: alertsData } = useQuery<AlertsData>({
    queryKey: ['network', 'alerts', selectedSiteId],
    queryFn: () =>
      apiClient.get(`/network/alerts/${selectedSiteId}`).then((r) => r.data),
    enabled: !isAllSites,
    refetchInterval: 60_000,
  })

  const { data: clientsCountData } = useQuery<ClientsCountData>({
    queryKey: ['network', 'clients-count', selectedSiteId],
    queryFn: () =>
      apiClient.get(`/network/clients-count/${selectedSiteId}`).then((r) => r.data),
    enabled: !isAllSites,
    refetchInterval: 60_000,
  })

  const {
    data: debugData,
    isLoading: debugLoading,
    error: debugError,
  } = useQuery<UnifiDebugData>({
    queryKey: ['network', 'debug'],
    queryFn: () => apiClient.get('/network/debug').then((r) => r.data),
    enabled: showDebug,
    retry: false,
    staleTime: 0,
  })

  // ─── NinjaOne data ───────────────────────────────────────────────────────────

  const {
    data: ninjaOrgs = [],
    isLoading: ninjaLoading,
    isError: ninjaError,
    refetch: ninjaRefetch,
    isFetching: ninjaFetching,
  } = useQuery<NinjaOrgDevices[]>({
    queryKey: ['ninja', 'all-devices'],
    queryFn: () => getAllNinjaDevices().then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: false,
  })

  interface NinjaDeviceRow extends NinjaDevice {
    customerId: string
    customerName: string
  }

  const ninjaRows: NinjaDeviceRow[] = useMemo(
    () =>
      ninjaOrgs.flatMap((org) =>
        org.devices.map((d) => ({ ...d, customerId: org.customerId, customerName: org.customerName })),
      ),
    [ninjaOrgs],
  )
  const ninjaOrgsWithError = ninjaOrgs.filter((o) => o.error)
  const ninjaOnlineCount = ninjaRows.filter((d) => d.online).length

  const ninjaColumns: ColumnDef<NinjaDeviceRow>[] = [
    {
      id: 'status',
      header: 'Status',
      size: 90,
      cell: ({ row }) => <StatusBadge status={row.original.online ? 'online' : 'offline'} size="sm" />,
    },
    {
      accessorKey: 'displayName',
      header: 'Gerät',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.displayName || row.original.systemName || `#${row.original.id}`}
        </span>
      ),
    },
    {
      accessorKey: 'systemName',
      header: 'Hostname',
      cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.systemName ?? '–'}</span>,
    },
    {
      id: 'customer',
      header: 'Kunde',
      accessorFn: (row) => row.customerName,
      cell: ({ row }) => (
        <Link to={`/kunden/${row.original.customerId}`} className="text-brand-600 hover:underline text-sm">
          {row.original.customerName}
        </Link>
      ),
    },
    {
      accessorKey: 'lastContact',
      header: 'Letzter Kontakt',
      cell: ({ row }) => {
        const ts = row.original.lastContact
        if (!ts) return <span className="text-gray-400 text-sm">–</span>
        const d = new Date(ts * 1000)
        return (
          <span className={cn('text-sm', !row.original.online && 'text-gray-400')}>
            {d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )
      },
    },
  ]

  // ─── Derived display data ─────────────────────────────────────────────────

  const displayDevices: DeviceWithSite[] = useMemo(() => {
    if (isAllSites) {
      return (allOverview?.sites ?? []).flatMap((site) =>
        site.devices.map((d) => ({ ...d, siteName: site.name, siteId: site.id })),
      )
    }
    return siteDevices
  }, [isAllSites, allOverview, siteDevices])

  const displayAlerts: Alert[] = isAllSites
    ? (allOverview?.sites ?? []).flatMap((s) => s.alerts)
    : (alertsData?.alerts ?? [])

  const displayAlertCount = isAllSites
    ? (allOverview?.totals?.alerts ?? 0)
    : (alertsData?.total ?? 0)

  const displayClientsCount = isAllSites
    ? (allOverview?.totals?.clients ?? 0)
    : (clientsCountData?.count ?? 0)

  const onlineCount = isAllSites
    ? (allOverview?.totals?.online ?? 0)
    : displayDevices.filter((d) => d.status === 'online').length

  const isLoading = isAllSites
    ? sitesLoading || allOverviewLoading
    : sitesLoading || devicesLoading

  const selectedSite = selectedSiteId ? sites.find((s) => s.id === selectedSiteId) : null
  const openDevice = openDeviceId ? displayDevices.find((d) => d.mac === openDeviceId) : undefined

  // ─── Error handling ───────────────────────────────────────────────────────

  const sitesErrCode = sitesErrorObj ? apiErrCode(sitesErrorObj) : undefined
  const configError = unifiStatus && !unifiStatus.configured ? unifiStatus.error : null
  const connectError =
    unifiStatus && unifiStatus.configured && !unifiStatus.reachable ? unifiStatus.error : null
  const effectiveConfigError =
    configError ??
    (sitesErrCode === 'UNIFI_NOT_CONFIGURED' ? apiErrMsg(sitesErrorObj) : null)
  const sitesErrMsg =
    sitesError && sitesErrCode !== 'UNIFI_NOT_CONFIGURED' ? apiErrMsg(sitesErrorObj) : null
  const devicesErrMsg = !isAllSites && devicesError ? apiErrMsg(devicesErrorObj) : null
  const allOverviewErrMsg =
    isAllSites && allOverviewError ? apiErrMsg(allOverviewErrorObj) : null

  // Warning when device data comes from cloud stubs (console unreachable via proxy)
  const devicesStubWarning: string | null = (() => {
    if (isAllSites) {
      const warnings = (allOverview?.sites ?? []).map((s) => s.devicesWarning).filter(Boolean)
      return warnings.length > 0 ? (warnings[0] as string) : null
    }
    // For single-site view: detect stub data by empty MACs
    const isStub = siteDevices.length > 0 && siteDevices.every((d) => !d.mac)
    return isStub
      ? 'Geräteliste unvollständig: Die UniFi-Konsole ist aktuell nicht über die Cloud erreichbar. MAC-Adressen und Echtzeit-Status stehen nicht zur Verfügung. Bitte prüfen Sie die Cloud-Verbindung der UniFi-Konsole.'
      : null
  })()

  // ─── Site name lookup for alarm table ────────────────────────────────────

  const siteMap = useMemo<Record<string, string>>(() => {
    if (isAllSites) {
      return Object.fromEntries((allOverview?.sites ?? []).map((s) => [s.id, s.name]))
    }
    return selectedSite ? { [selectedSite.id]: selectedSite.name } : {}
  }, [isAllSites, allOverview, selectedSite])

  // ─── Device Columns ───────────────────────────────────────────────────────

  const deviceColumns = useMemo<ColumnDef<DeviceWithSite, unknown>[]>(
    () => [
      ...(isAllSites
        ? [
            {
              id: 'kunde',
              accessorFn: (d: DeviceWithSite) => d.siteName ?? '–',
              header: 'Kunde',
              cell: ({ row }: { row: { original: DeviceWithSite } }) => (
                <span className="text-small font-medium text-brand-600">
                  {row.original.siteName ?? <span className="text-gray-400">–</span>}
                </span>
              ),
            } as ColumnDef<DeviceWithSite, unknown>,
          ]
        : []),
      {
        id: 'name',
        accessorFn: (d) => d.name ?? d.mac,
        header: 'Gerät',
        cell: ({ row }) => {
          const d = row.original
          return (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  d.status === 'online' ? 'bg-green-500' : 'bg-red-400',
                )}
              />
              <span className="font-medium text-gray-900">{d.name ?? d.mac}</span>
            </div>
          )
        },
      },
      {
        id: 'type',
        accessorFn: (d) => d.type,
        header: 'Typ',
        cell: ({ row }) => (
          <Badge variant="info" size="sm">
            {DEVICE_TYPE_LABEL[row.original.type] ?? row.original.type}
          </Badge>
        ),
      },
      {
        id: 'model',
        accessorFn: (d) => d.model ?? '–',
        header: 'Modell',
        cell: ({ row }) => (
          <span className="font-mono text-small text-gray-700">
            {row.original.model ?? <span className="text-gray-400">–</span>}
          </span>
        ),
      },
      {
        id: 'ip',
        accessorFn: (d) => d.ip ?? '–',
        header: 'IP',
        cell: ({ row }) => (
          <span className="font-mono text-small text-gray-700">
            {row.original.ip ?? <span className="text-gray-400">–</span>}
          </span>
        ),
      },
      {
        id: 'firmware',
        header: 'Firmware',
        cell: ({ row }) => {
          const d = row.original
          if (!d.version) return <span className="text-gray-400">–</span>
          return (
            <Badge variant={d.updateAvailable ? 'warning' : 'success'} size="sm">
              {d.version}
              {d.updateAvailable ? ' (veraltet)' : ''}
            </Badge>
          )
        },
      },
      {
        id: 'clients',
        header: 'Clients',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.num_sta ?? 0}</span>
        ),
      },
      {
        id: 'uptime',
        header: 'Uptime',
        cell: ({ row }) => {
          const u = row.original.uptime
          return u !== undefined ? (
            <span className="text-gray-700">{formatUptime(u)}</span>
          ) : (
            <span className="text-gray-400">–</span>
          )
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.status} size="sm" />,
      },
      {
        id: 'details',
        header: '',
        size: 90,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpenDeviceId(row.original.mac)}
          >
            Details →
          </Button>
        ),
      },
    ],
    [isAllSites],
  )

  // ─── Alarm Columns ────────────────────────────────────────────────────────

  const alarmColumns = useMemo<ColumnDef<Alert, unknown>[]>(
    () => [
      {
        id: 'severity',
        header: 'Schweregrad',
        cell: ({ row }) => {
          const s = row.original.severity
          return (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-8 w-1 shrink-0 rounded-full',
                  s === 'critical' && 'bg-red-500',
                  s === 'warning' && 'bg-yellow-500',
                  s === 'info' && 'bg-blue-500',
                )}
              />
              <Badge
                variant={s === 'critical' ? 'danger' : s === 'warning' ? 'warning' : 'info'}
                size="sm"
              >
                {s === 'critical' ? 'Kritisch' : s === 'warning' ? 'Warnung' : 'Info'}
              </Badge>
            </div>
          )
        },
      },
      {
        id: 'event',
        header: 'Ereignis',
        cell: ({ row }) => {
          const a = row.original
          return (
            <div>
              <p className="font-medium text-gray-900">{a.msg}</p>
              <p className="text-caption text-gray-500">
                {new Date(a.datetime).toLocaleString('de-DE')}
              </p>
            </div>
          )
        },
      },
      {
        id: 'device',
        header: 'Gerät',
        cell: ({ row }) => {
          const a = row.original
          const dev = a.device_mac ? displayDevices.find((d) => d.mac === a.device_mac) : undefined
          return (
            <span className="font-mono text-small text-gray-700">
              {dev?.name ?? a.device_mac ?? <span className="text-gray-400">–</span>}
            </span>
          )
        },
      },
      {
        id: 'kunde',
        header: 'Kunde',
        cell: ({ row }) => {
          const name = siteMap[row.original.site_id]
          return <span className="text-gray-700">{name ?? row.original.site_id ?? '–'}</span>
        },
      },
      {
        id: 'alarmDetails',
        header: '',
        size: 90,
        cell: ({ row }) => {
          const mac = row.original.device_mac
          if (!mac) return null
          return (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpenDeviceId(mac)}
            >
              Details →
            </Button>
          )
        },
      },
    ],
    [displayDevices, siteMap],
  )

  // ─── Row class for alarm left-border ─────────────────────────────────────

  const deviceRowClassName = (device: DeviceWithSite): string => {
    const alarm = getAlarmForDevice(device, displayAlerts)
    if (!alarm) return ''
    return alarm.severity === 'critical'
      ? 'border-l-[3px] border-l-red-500'
      : 'border-l-[3px] border-l-yellow-500'
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (effectiveConfigError) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <Network className="h-5 w-5 text-brand-500" />
              Netzwerk · UniFi
            </span>
          }
        />
        <ErrorState
          title="UniFi nicht konfiguriert"
          description={
            <span>
              {effectiveConfigError}
              <br />
              <a
                href="/einstellungen"
                className="mt-1 inline-block text-brand-500 underline hover:text-brand-700"
              >
                → Einstellungen → Globale Adapter → UniFi → API-Key eintragen
              </a>
            </span>
          }
        />
      </div>
    )
  }

  const siteOptions = [
    { value: ALL_SITES_VALUE, label: 'Alle Kunden' },
    ...sites.map((s) => ({ value: s.id, label: s.name })),
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Network className="h-5 w-5 text-brand-500" />
            Netzwerk · UniFi
            {isAllSites && (
              <Badge variant="info" size="sm" className="ml-1">
                Gesamtübersicht
              </Badge>
            )}
          </span>
        }
        actions={
          sites.length > 0 ? (
            <Select
              value={selectedSiteId ?? ALL_SITES_VALUE}
              onValueChange={(v) => setSelectedSiteId(v === ALL_SITES_VALUE ? null : v)}
              options={siteOptions}
              placeholder="Kunde wählen …"
              triggerClassName="w-52"
            />
          ) : undefined
        }
      />

      {connectError && (
        <ErrorState title="UniFi API nicht erreichbar" description={connectError} />
      )}

      {sitesErrMsg && !connectError && (
        <ErrorState
          title="UniFi-Daten konnten nicht geladen werden"
          details={sitesErrMsg}
        />
      )}

      {allOverviewErrMsg && (
        <div className="space-y-3">
          <ErrorState
            title="Gesamtübersicht konnte nicht geladen werden"
            details={allOverviewErrMsg}
          />
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-small font-medium text-gray-700">UniFi API-Diagnose</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDebug((v) => !v)}
              >
                {showDebug ? 'Diagnose ausblenden' : 'Diagnose laden'}
              </Button>
            </div>
            {showDebug && (
              <div className="mt-3 space-y-2">
                {debugLoading && <p className="text-small text-gray-500">Lade Diagnosedaten …</p>}
                {debugError && (
                  <p className="text-small text-red-600">Diagnosefehler: {apiErrMsg(debugError)}</p>
                )}
                {debugData && (
                  <>
                    <p className="text-small font-semibold text-gray-700">Erkannte Sites &amp; Host-IDs:</p>
                    <div className="overflow-auto rounded border border-gray-200 bg-white p-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-left text-gray-500">
                            <th className="pb-1 pr-4">Site-ID</th>
                            <th className="pb-1 pr-4">internalId</th>
                            <th className="pb-1 pr-4">rawHostId</th>
                            <th className="pb-1 pr-4">proxyHostId</th>
                            <th className="pb-1">macFallback</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debugData.computedSites.map((s) => (
                            <tr key={s.siteId} className="border-b last:border-0">
                              <td className="py-1 pr-4 font-mono">{s.siteId}</td>
                              <td className="py-1 pr-4 font-mono">{s.internalId}</td>
                              <td className="py-1 pr-4 font-mono text-gray-500 break-all max-w-xs">{s.rawHostId}</td>
                              <td className="py-1 pr-4 font-mono">{s.proxyHostId}</td>
                              <td className="py-1 font-mono text-orange-600">{s.macOnlyHostId ?? '–'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {debugData.proxyPathTest && debugData.proxyPathTest.length > 0 && (
                      <>
                        <p className="text-small font-semibold text-gray-700 mt-2">Proxy-Pfad-Test (erste Site):</p>
                        <div className="overflow-auto rounded border border-gray-200 bg-white p-2">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b text-left text-gray-500">
                                <th className="pb-1 pr-4">Label</th>
                                <th className="pb-1 pr-4">Status</th>
                                <th className="pb-1">Detail / Fehler</th>
                              </tr>
                            </thead>
                            <tbody>
                              {debugData.proxyPathTest.map((t) => (
                                <tr key={t.label} className="border-b last:border-0">
                                  <td className="py-1 pr-4 font-mono">{t.label}</td>
                                  <td className={`py-1 pr-4 font-mono font-semibold ${t.result === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                                    {t.result === 'ok' ? '✓ OK' : `✗ ${t.status ?? 'ERR'}`}
                                  </td>
                                  <td className="py-1 font-mono text-gray-600 break-all max-w-sm">{t.detail}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                    <p className="text-small font-semibold text-gray-700 mt-2">Hosts ({debugData.hosts.length}):</p>
                    <pre className="overflow-auto rounded border border-gray-200 bg-white p-2 text-xs max-h-48">
                      {JSON.stringify(debugData.hosts, null, 2)}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {devicesStubWarning && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <p className="text-small text-amber-700">{devicesStubWarning}</p>
        </div>
      )}

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile
          icon={<Server className="h-5 w-5" />}
          label="Geräte gesamt"
          value={isLoading ? '…' : displayDevices.length}
          iconClass="text-brand-500"
        />
        <KpiTile
          icon={<CheckCircle className="h-5 w-5" />}
          label="Online"
          value={isLoading ? '…' : onlineCount}
          iconClass="text-green-500"
        />
        <KpiTile
          icon={<Users className="h-5 w-5" />}
          label="Aktive Clients"
          value={isLoading ? '…' : displayClientsCount}
          iconClass="text-brand-500"
        />
        <KpiTile
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Offene Alarme"
          value={isLoading ? '…' : displayAlertCount}
          iconClass={displayAlertCount > 0 ? 'text-red-500' : 'text-gray-400'}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="uebersicht">
        <TabsList>
          <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
          <TabsTrigger value="alarme">
            Alarme
            {displayAlertCount > 0 && (
              <Badge variant="danger" size="sm" className="ml-1">
                {displayAlertCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ninja">
            <MonitorSmartphone className="mr-1 h-3.5 w-3.5" />
            NinjaOne
            {!ninjaLoading && ninjaRows.length > 0 && (
              <Badge variant="info" size="sm" className="ml-1">{ninjaOnlineCount}/{ninjaRows.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="uebersicht">
          {devicesErrMsg ? (
            <div className="space-y-3">
              <ErrorState
                title="Geräte konnten nicht geladen werden"
                details={devicesErrMsg}
              />
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-small font-medium text-gray-700">UniFi API-Diagnose</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowDebug((v) => !v)}
                  >
                    {showDebug ? 'Diagnose ausblenden' : 'Diagnose laden'}
                  </Button>
                </div>
                {showDebug && (
                  <div className="mt-3 space-y-2">
                    {debugLoading && <p className="text-small text-gray-500">Lade Diagnosedaten …</p>}
                    {debugError && (
                      <p className="text-small text-red-600">Diagnosefehler: {apiErrMsg(debugError)}</p>
                    )}
                    {debugData && (
                      <>
                        <p className="text-small font-semibold text-gray-700">Erkannte Sites &amp; Host-IDs:</p>
                        <div className="overflow-auto rounded border border-gray-200 bg-white p-2">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b text-left text-gray-500">
                                <th className="pb-1 pr-4">Site-ID</th>
                                <th className="pb-1 pr-4">internalId</th>
                                <th className="pb-1 pr-4">rawHostId</th>
                                <th className="pb-1 pr-4">proxyHostId</th>
                                <th className="pb-1">macFallback</th>
                              </tr>
                            </thead>
                            <tbody>
                              {debugData.computedSites.map((s) => (
                                <tr key={s.siteId} className="border-b last:border-0">
                                  <td className="py-1 pr-4 font-mono">{s.siteId}</td>
                                  <td className="py-1 pr-4 font-mono">{s.internalId}</td>
                                  <td className="py-1 pr-4 font-mono text-gray-500 break-all max-w-xs">{s.rawHostId}</td>
                                  <td className="py-1 pr-4 font-mono">{s.proxyHostId}</td>
                                  <td className="py-1 font-mono text-orange-600">{s.macOnlyHostId ?? '–'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {debugData.proxyPathTest && debugData.proxyPathTest.length > 0 && (
                          <>
                            <p className="text-small font-semibold text-gray-700 mt-2">Proxy-Pfad-Test (erste Site):</p>
                            <div className="overflow-auto rounded border border-gray-200 bg-white p-2">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b text-left text-gray-500">
                                    <th className="pb-1 pr-4">Label</th>
                                    <th className="pb-1 pr-4">Status</th>
                                    <th className="pb-1">Detail / Fehler</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {debugData.proxyPathTest.map((t) => (
                                    <tr key={t.label} className="border-b last:border-0">
                                      <td className="py-1 pr-4 font-mono">{t.label}</td>
                                      <td className={`py-1 pr-4 font-mono font-semibold ${t.result === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                                        {t.result === 'ok' ? '✓ OK' : `✗ ${t.status ?? 'ERR'}`}
                                      </td>
                                      <td className="py-1 font-mono text-gray-600 break-all max-w-sm">{t.detail}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                        <p className="text-small font-semibold text-gray-700 mt-2">Hosts ({debugData.hosts.length}):</p>
                        <pre className="overflow-auto rounded border border-gray-200 bg-white p-2 text-xs max-h-48">
                          {JSON.stringify(debugData.hosts, null, 2)}
                        </pre>
                        <p className="text-small font-semibold text-gray-700 mt-2">Sites-Rohdaten ({debugData.sites.length}):</p>
                        <pre className="overflow-auto rounded border border-gray-200 bg-white p-2 text-xs max-h-48">
                          {JSON.stringify(debugData.sites, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <DataTable<DeviceWithSite>
              data={displayDevices}
              columns={deviceColumns}
              getRowId={(d) => d.mac}
              loading={isLoading}
              rowClassName={deviceRowClassName}
              emptyState={
                <EmptyState
                  icon={<Server />}
                  title="Keine Geräte gefunden"
                  description={
                    isAllSites
                      ? 'Keine UniFi-Geräte in allen konfigurierten Sites gefunden.'
                      : 'Für die gewählte Site sind keine UniFi-Geräte verfügbar.'
                  }
                />
              }
            />
          )}
        </TabsContent>

        <TabsContent value="alarme">
          <DataTable<Alert>
            data={displayAlerts}
            columns={alarmColumns}
            getRowId={(a) => a._id}
            emptyState={
              <EmptyState
                icon={<AlertTriangle />}
                title="Keine offenen Alarme"
                description="Alle Alarme sind archiviert oder es liegen keine Ereignisse vor."
              />
            }
          />
        </TabsContent>

        <TabsContent value="ninja">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-small text-gray-600">
                {ninjaLoading
                  ? 'NinjaOne-Geräte werden geladen…'
                  : `${ninjaOnlineCount} von ${ninjaRows.length} Geräten online`}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void ninjaRefetch()}
                disabled={ninjaFetching}
              >
                {ninjaFetching ? 'Lädt…' : 'Aktualisieren'}
              </Button>
            </div>

            {ninjaError && (
              <ErrorState
                title="NinjaOne-Geräte konnten nicht geladen werden"
                description="Bitte prüfe die NinjaOne-Konfiguration unter Einstellungen."
              />
            )}

            {ninjaOrgsWithError.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 space-y-1">
                <p className="font-medium">Fehler bei einzelnen Kunden:</p>
                {ninjaOrgsWithError.map((o) => (
                  <p key={o.customerId}><strong>{o.customerName}:</strong> {o.error}</p>
                ))}
              </div>
            )}

            <DataTable<NinjaDeviceRow>
              data={ninjaRows}
              columns={ninjaColumns}
              getRowId={(d) => String(d.id)}
              searchPlaceholder="Gerät oder Kunde suchen…"
              emptyState={
                ninjaLoading
                  ? undefined
                  : (
                    <EmptyState
                      icon={<MonitorSmartphone />}
                      title="Keine NinjaOne-Geräte gefunden"
                      description="Noch keine Kunden mit NinjaOne synchronisiert. Öffne einen Kunden und nutze den NinjaOne-Sync-Button."
                    />
                  )
              }
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Device Detail Sheet */}
      <Sheet
        open={!!openDeviceId}
        onOpenChange={(open) => {
          if (!open) setOpenDeviceId(null)
        }}
      >
        <SheetContent>
          <DeviceSheet device={openDevice} alerts={displayAlerts} />
        </SheetContent>
      </Sheet>
    </div>
  )
}
